/**
 * BuildingRows — the "build the brain" checklist for onboarding step 4.
 *
 * Each row now reflects REAL work. It used to step on a 3.4-second timer with
 * no backend behind it; a row is now `waiting`, `working`, `done`, `failed` or
 * `skipped`, driven by `useOnboardingBuild`, and it only ever shows what
 * actually happened.
 *
 * Presentational only — it renders the stages it is handed.
 */
import { AlertCircle, Check, Loader2, Minus } from "lucide-react";
import type { OnboardingBuildStage } from "@/types/voice-vision/launch";

interface BuildingRowsProps {
  stages: OnboardingBuildStage[];
}

/** The disc at the left of each row, per state. */
const StateDisc = ({ state }: { state: OnboardingBuildStage["state"] }) => {
  if (state === "done") {
    return (
      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
        <Check className="w-[15px] h-[15px] text-white" strokeWidth={2.4} />
      </div>
    );
  }

  if (state === "working") {
    return (
      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center bg-white border-2 border-genie-primary">
        <Loader2 className="w-[15px] h-[15px] text-genie-primary animate-spin" />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center bg-white border-2 border-genie-secondary">
        <AlertCircle className="w-[15px] h-[15px] text-genie-secondary" strokeWidth={2.2} />
      </div>
    );
  }

  if (state === "skipped") {
    return (
      <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center bg-genie-background">
        <Minus className="w-[13px] h-[13px] text-genie-muted" strokeWidth={2.4} />
      </div>
    );
  }

  return (
    <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center bg-genie-background">
      <span className="w-[7px] h-[7px] rounded-full bg-genie-muted" />
    </div>
  );
};

export const BuildingRows = ({ stages }: BuildingRowsProps) => (
  <div className="w-full max-w-[480px] mx-auto flex flex-col gap-3">
    {stages.map((stage) => {
      const isActive = stage.state === "working";
      const isFailed = stage.state === "failed";
      const isDim = stage.state === "waiting" || stage.state === "skipped";

      return (
        <div
          key={stage.id}
          className={`flex items-start gap-3.5 p-4 rounded-2xl border transition-all duration-300 ${
            isActive
              ? "border-genie-primary/35 bg-genie-primary-light ring-4 ring-genie-primary/[0.06]"
              : isFailed
                ? "border-genie-secondary/35 bg-white"
                : "border-genie-border bg-white"
          }`}
        >
          <StateDisc state={stage.state} />

          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isDim ? "text-genie-muted" : "text-genie-text"}`}>
              {stage.label}
            </p>
            <p className="text-xs text-genie-muted leading-snug">{stage.sub}</p>
          </div>

          {isActive && (
            <span className="text-[11.5px] font-semibold text-genie-primary flex-shrink-0 mt-0.5">
              Working...
            </span>
          )}
          {stage.state === "skipped" && (
            <span className="text-[11.5px] font-semibold text-genie-muted flex-shrink-0 mt-0.5">
              Skipped
            </span>
          )}
        </div>
      );
    })}
  </div>
);

export default BuildingRows;
